import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";

type FindCachesProps = {
  search?: string;
  searchKeys?: string[];
};

export const useApi = () => {
  const queryClient = useQueryClient();

  /**
   * @description It finds all cache data related to the keyword.
   * @param search string - The keyword to search for in the cache.
   * @param searchKeys string[] - The keywords to search for in the cache.
   * @example findCaches({ search: "invoice" });
   * @example findCaches({ searchKeys: ["user", "invoice"] });
   * @returns string[] - The keys of the cache data.
   */
  const findCaches = ({ search, searchKeys }: FindCachesProps) => {
    if (!search && !searchKeys) return;

    const cache = queryClient.getQueryCache().findAll();

    const _keys = searchKeys || (search ? [search] : []);
    const find = cache
      // If it is older than 2 seconds, return false
      .filter((c) => {
        const date = c.state.dataUpdatedAt;
        if (date && dayjs().diff(dayjs(date), "second") <= 2) {
          return false;
        }
        return true;
      })
      .filter((c) => {
        const cacheKeys = c.queryKey as string[];
        return _keys.some((key) => cacheKeys.includes(key));
      })
      .map((c) => {
        return c.queryKey;
      })
      .flat()
      .filter((c) => c !== undefined);
    return new Set(find);
  };

  /**
   * @description It refetches all cache data related to the keyword.
   * @param search string - The keyword to search for in the cache.
   * @param searchKeys string[] - The keywords to search for in the cache.
   * @example refetchCaches({ search: "invoice" });
   * @example refetchCaches({ searchKeys: ["user", "invoice"] });
   * @returns
   */
  const refetchCaches = ({ search, searchKeys }: FindCachesProps) => {
    const keys = findCaches({ search, searchKeys });

    keys.forEach((key) => {
      queryClient.refetchQueries({
        queryKey: [key],
      });
    });
  };

  /**
   * @description It scans, finds and deletes all cache data related to the keyword.
   * @param search string - The keyword to search for in the cache.
   * @param searchKeys string[] - The keywords to search for in the cache.
   * @example queriesCacheClear({ search: "invoice" });
   * @example queriesCacheClear({ searchKeys: ["user", "invoice"] });
   * @returns
   */
  const queriesCacheClear = ({ search, searchKeys }: FindCachesProps) => {
    const keys = findCaches({ search, searchKeys });

    keys.forEach((key) => {
      queryClient.removeQueries({
        queryKey: [key],
      });
    });
    return keys;
  };

  useEffect(() => {
    return () => {};
  }, []);

  return {
    findCaches,
    queriesCacheClear,
    refetchCaches,
  } as const;
};
